'use client';

import Script from 'next/script';
import { useMemo } from 'react';
import {
  buildMarketingWidgetConfig,
  escapeScriptJson,
  resolveWidgetBaseUrl,
  type WidgetConfigEnv,
} from '@/lib/widget-config';

const widgetEnv: WidgetConfigEnv = {
  NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
  NEXT_PUBLIC_WIDGET_BASE_URL: process.env.NEXT_PUBLIC_WIDGET_BASE_URL,
  NEXT_PUBLIC_WIDGET_ENABLED: process.env.NEXT_PUBLIC_WIDGET_ENABLED,
  NEXT_PUBLIC_WIDGET_TENANT_ID: process.env.NEXT_PUBLIC_WIDGET_TENANT_ID,
};

export function WidgetEmbed({ nonce, locale }: { nonce?: string; locale?: string }) {
  const widgetLocale = locale === 'ar' ? 'ar' : 'en';
  const widgetConfig = useMemo(
    () => buildMarketingWidgetConfig(widgetEnv, widgetLocale),
    [widgetLocale],
  );

  if (!widgetConfig) {
    return null;
  }

  const baseUrl = resolveWidgetBaseUrl(widgetEnv);

  return (
    <>
      {/* suppressHydrationWarning: browsers hide the `nonce` attribute from the
          DOM after parsing it (CSP spec, to stop it leaking via attribute
          selectors), so React sees server nonce="…" vs client nonce="" on
          every load and would otherwise run hydration recovery. */}
      <script
        nonce={nonce}
        suppressHydrationWarning
        dangerouslySetInnerHTML={{
          __html: `window.__kasbly = ${escapeScriptJson(JSON.stringify(widgetConfig))};`,
        }}
      />
      <Script
        nonce={nonce}
        suppressHydrationWarning
        src={`${baseUrl}/widget/v1/widget.js`}
        strategy="afterInteractive"
        onLoad={() => {
          // widget.js binds its init to DOMContentLoaded, but Next injects this
          // script after that event has already fired — so init would never run.
          // Re-dispatch it once the script is loaded to kick off the widget.
          // (The underlying widget.js should guard on document.readyState.)
          if (typeof document !== 'undefined' && document.readyState !== 'loading') {
            window.dispatchEvent(new Event('DOMContentLoaded'));
          }
        }}
      />
    </>
  );
}
